import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { competenciaService } from '@/services/competenciaService'
import { FechamentoCompetencia, MesCompetencia, PendenciasFechamento } from '@/types/competencia'
import { brl } from './estadoFinanceiro'
import { codigoErro, dadosDoErro } from './errosNotaFiscal'

/** `aaaa-mm` → `mm/aaaa` (só formatação). */
export const mesCompetencia = (competencia: MesCompetencia | null | undefined): string => {
  if (!competencia) return '—'
  const [ano, mes] = competencia.split('-')
  return mes && ano ? `${mes}/${ano}` : competencia
}

/** Mensagem do erro do fechamento: a do backend; com projetadas, os meses que o backend listou. */
export const mensagemErroFechamento = (e: unknown, padrao: string): string => {
  const msg = (e as { message?: unknown } | null)?.message
  const base = typeof msg === 'string' && msg.trim() ? msg : padrao
  if (codigoErro(e) === 'COMPETENCIA_COM_PROJETADAS') {
    const meses = dadosDoErro<{ competencias: unknown }>(e)?.competencias
    if (Array.isArray(meses) && meses.length > 0) {
      return `${base} Competências com comissões projetadas: ${meses.map((m) => mesCompetencia(String(m))).join(', ')}.`
    }
  }
  return base
}

interface Props {
  /** Competência a fechar (`aaaa-mm`); nula = diálogo fechado. */
  competencia: MesCompetencia | null
  /** Do `GET /competencias`: nenhum fechamento ainda — o primeiro fecha também todo o passado. */
  semFechamentoAinda: boolean
  onFechar: () => void
  onFechada: (resposta: FechamentoCompetencia) => void
}

/**
 * "Fechar competência" (AN-PP5 §5.1, §9). Mostra as pendências que o backend devolve
 * (`/pendencias-fechamento`: PROJETADOS que impedem o fechamento, com o total já somado lá) e
 * avisa que, depois de fechada, a competência não muda. No primeiro fechamento
 * (`semFechamentoAinda`) avisa que ele fecha também todas as competências anteriores.
 *
 * Quem decide se pode fechar é o backend: o botão só existe quando `podeFechar` veio verdadeiro, e
 * qualquer recusa (em curso, fora de sequência, projetadas, concorrente) aparece com a mensagem dele.
 */
const FecharCompetenciaDialog: FC<Props> = ({ competencia, semFechamentoAinda, onFechar, onFechada }) => {
  const [pendencias, setPendencias] = useState<PendenciasFechamento | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [erroPendencias, setErroPendencias] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    setErro(null)
    setErroPendencias(null)
    setPendencias(null)
    if (!competencia) return
    let ativo = true
    setCarregando(true)
    competenciaService
      .pendencias(competencia)
      .then((p) => {
        if (ativo) setPendencias(p)
      })
      .catch((e) => {
        if (ativo) {
          setErroPendencias(
            (e as { message?: string })?.message ?? 'Não foi possível carregar as pendências do fechamento.'
          )
        }
      })
      .finally(() => {
        if (ativo) setCarregando(false)
      })
    return () => {
      ativo = false
    }
  }, [competencia])

  const fechar = async () => {
    if (!competencia) return
    setSalvando(true)
    setErro(null)
    try {
      onFechada(await competenciaService.fechar(competencia))
    } catch (e) {
      setErro(mensagemErroFechamento(e, 'Falha ao fechar a competência.'))
    } finally {
      setSalvando(false)
    }
  }

  const comPendencias = (pendencias?.quantidade ?? 0) > 0

  return (
    <Dialog open={competencia !== null} onClose={onFechar} fullWidth maxWidth="sm">
      <DialogTitle>Fechar competência {mesCompetencia(competencia)}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {erro && <Alert severity="error">{erro}</Alert>}
          <Typography variant="body2">
            Depois de fechada, a competência não muda: ajustes caem na competência corrente.
          </Typography>
          {semFechamentoAinda && (
            <Alert severity="warning">
              Este é o primeiro fechamento: isto fecha também todas as competências anteriores a{' '}
              {mesCompetencia(competencia)}. Revise os meses anteriores antes de confirmar.
            </Alert>
          )}
          {carregando ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} aria-label="Carregando pendências" />
              <Typography variant="body2">Conferindo pendências do fechamento…</Typography>
            </Stack>
          ) : erroPendencias ? (
            <Alert severity="error">{erroPendencias}</Alert>
          ) : comPendencias && pendencias ? (
            <>
              <Alert severity="error">
                {pendencias.quantidade} comissão(ões) projetada(s) impedem o fechamento (total{' '}
                {brl(pendencias.totalProjetado)}). Confirme ou cancele antes de fechar.
              </Alert>
              <Table size="small" aria-label="Pendências do fechamento">
                <TableHead>
                  <TableRow>
                    <TableCell>Competência</TableCell>
                    <TableCell>Reserva</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Projetado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendencias.itens.map((p) => (
                    <TableRow key={p.lancamentoId}>
                      <TableCell>{dayjs(p.competencia).format('MM/YYYY')}</TableCell>
                      <TableCell>{p.reservaNumero != null ? `RES-${p.reservaNumero}` : '—'}</TableCell>
                      <TableCell>{p.razaoSocial ?? '—'}</TableCell>
                      <TableCell align="right">{brl(p.valorProjetado)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : (
            pendencias && (
              <Alert severity="success">Nenhuma comissão projetada pendente nesta competência.</Alert>
            )
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Voltar</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => void fechar()}
          disabled={salvando || carregando || comPendencias}
        >
          {salvando ? 'Fechando…' : 'Fechar competência'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FecharCompetenciaDialog
